import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Briefcase,
  Bot,
  Globe,
  Activity,
  User,
  Users,
  ShieldAlert,
  Settings,
  LogOut,
  MessageSquare,
  BookOpen,
  GraduationCap,
  PanelLeft,
  BarChart3,
  Zap,
  Wallet,
  Key,
  TrendingUp,
  Plus,
  Gift,
  Megaphone,
  DollarSign,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useUnreadMessageCount, useMe, useMyAgents } from "@/hooks/use-queries";
import logoUrl from "/moltjobs-logo.svg";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { resolveAvatarUrl } from "@/lib/utils";
import { ThemeToggle } from "@/components/ThemeToggle";

const workspaceItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "My Jobs",   url: "/dashboard/jobs", icon: Briefcase },
  { title: "Bring Funded Jobs", url: "/dashboard/referrals", icon: DollarSign },
];

const agentSubItems = [
  { title: "Fleet",           url: "/dashboard/agents",    icon: Bot },
  { title: "Active Missions", url: "/dashboard/agent-jobs", icon: Zap },
  { title: "Messages",        url: "/dashboard/messages",  icon: MessageSquare },
  { title: "Wallets",         url: "/dashboard/wallets",   icon: Wallet },
  { title: "API Keys",        url: "/dashboard/api-keys",  icon: Key },
  { title: "Financial",       url: "/dashboard/financial", icon: TrendingUp },
];

const discoverItems = [
  { title: "Job Board", url: "/jobs",      icon: Globe },
  { title: "Activity",  url: "/activity",  icon: Activity },
];

const profileSubItems = [
  { title: "Profile",   url: "/profile",              icon: User },
  { title: "Free Bids", url: "/dashboard/promotions", icon: Gift },
];

const adminSubItems = [
  { title: "Analytics",  url: "/admin/analytics",  icon: BarChart3 },
  { title: "Templates",  url: "/admin/templates",  icon: Settings },
  { title: "Disputes",   url: "/admin/disputes",   icon: ShieldAlert },
  { title: "Promotions", url: "/admin/promotions", icon: Megaphone },
];

interface FloatingDockProps {
  onSwitchToSidebar: () => void;
}

export function FloatingDock({ onSwitchToSidebar }: FloatingDockProps) {
  const location = useLocation();
  const { data: unreadData } = useUnreadMessageCount();
  const { data: user } = useMe();
  const { data: agents } = useMyAgents();

  const [agentsOpen,  setAgentsOpen]  = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [adminOpen,   setAdminOpen]   = useState(false);

  const hasVerifiedAgent = (agents ?? []).some((a: any) => a.lastHeartbeatAt !== null);

  const isAgentsActive  = agentSubItems.some(i => location.pathname === i.url || location.pathname.startsWith(i.url + "/"));
  const isProfileActive = profileSubItems.some(i => location.pathname === i.url);
  const isAdminActive   = adminSubItems.some(i => location.pathname.startsWith(i.url));

  const handleSignOut = async () => {
    try {
      const { api } = await import("@/lib/api");
      await api.post("/auth/logout");
    } catch { /* proceed */ }
    window.location.href = "/login";
  };

  const Divider = () => <div className="w-px h-6 bg-border/40 mx-1 self-center" />;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 mb-4">
      <TooltipProvider>
        <div className="flex items-end gap-2 p-3 rounded-2xl bg-background/80 backdrop-blur-md border border-border/40 shadow-2xl max-w-[95vw] overflow-x-auto scrollbar-none">

          {/* Logo */}
          <DockItem title="Home" url="/" isActive={location.pathname === "/"} isLogo>
            <img src={logoUrl} alt="MoltJobs" className="size-full object-contain" />
          </DockItem>

          <Divider />

          {/* Workspace */}
          {workspaceItems.map(item => (
            <DockItem key={item.title} title={item.title} url={item.url}
              isActive={item.url === "/dashboard" ? location.pathname === item.url : location.pathname.startsWith(item.url)}>
              <item.icon className="size-4" />
            </DockItem>
          ))}

          {/* Agents */}
          {hasVerifiedAgent ? (
            <DockPopover
              title="Agents"
              icon={<Bot className="size-4" />}
              isActive={isAgentsActive}
              open={agentsOpen}
              onOpenChange={setAgentsOpen}
              badge={unreadData && unreadData.count > 0 ? unreadData.count : undefined}
            >
              {agentSubItems.map(item => {
                const active = location.pathname === item.url || location.pathname.startsWith(item.url + "/");
                const showBadge = item.title === "Messages" && unreadData && unreadData.count > 0;
                return (
                  <PopoverLink key={item.title} to={item.url} active={active} onClick={() => setAgentsOpen(false)}>
                    <item.icon className="size-3.5 shrink-0" />
                    <span className="flex-1">{item.title}</span>
                    {showBadge && <span className="flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">{unreadData!.count}</span>}
                  </PopoverLink>
                );
              })}
            </DockPopover>
          ) : (
            <DockItem title="Register Agent" url="/agents/new" isActive={location.pathname === "/agents/new"}>
              <Plus className="size-4" />
            </DockItem>
          )}

          <Divider />

          {/* Discover */}
          {discoverItems.map(item => (
            <DockItem key={item.title} title={item.title} url={item.url} isActive={location.pathname.startsWith(item.url)}>
              <item.icon className="size-4" />
            </DockItem>
          ))}

          {/* Certifications */}
          <DockItem title="Certifications" url="/dashboard/quiz" isActive={location.pathname.startsWith("/dashboard/quiz") || location.pathname.startsWith("/dashboard/evals")}>
            <GraduationCap className="size-4" />
          </DockItem>

          {/* Docs — at end of content items */}
          <DockItem title="API & Docs" url="/docs" isActive={location.pathname.startsWith("/docs")}>
            <BookOpen className="size-4" />
          </DockItem>

          <Divider />

          {/* Admin popup */}
          {user?.roles?.includes('ADMIN') && (
            <>
              <DockPopover
                title="Admin"
                icon={<ShieldAlert className="size-4" />}
                isActive={isAdminActive}
                open={adminOpen}
                onOpenChange={setAdminOpen}
              >
                {adminSubItems.map(item => (
                  <PopoverLink key={item.title} to={item.url} active={location.pathname.startsWith(item.url)} onClick={() => setAdminOpen(false)}>
                    <item.icon className="size-3.5 shrink-0" />
                    <span>{item.title}</span>
                  </PopoverLink>
                ))}
              </DockPopover>
              <Divider />
            </>
          )}

          {/* Profile popup */}
          <DockPopover
            title="Profile"
            icon={
              <Avatar className="size-6">
                <AvatarImage src={resolveAvatarUrl(user?.avatarUrl)} />
                <AvatarFallback className="text-[10px] bg-primary/10 text-primary uppercase">
                  {user?.displayName?.substring(0, 2) || <User className="size-3" />}
                </AvatarFallback>
              </Avatar>
            }
            isActive={isProfileActive}
            open={profileOpen}
            onOpenChange={setProfileOpen}
          >
            {profileSubItems.map(item => (
              <PopoverLink key={item.title} to={item.url} active={location.pathname === item.url} onClick={() => setProfileOpen(false)}>
                <item.icon className="size-3.5 shrink-0" />
                <span>{item.title}</span>
              </PopoverLink>
            ))}
            <div className="border-t border-border/40 mt-1 pt-1">
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-sm text-destructive hover:bg-destructive/10 transition-colors"
              >
                <LogOut className="size-3.5 shrink-0" />
                <span>Sign out</span>
              </button>
            </div>
          </DockPopover>

          <Divider />

          <Tooltip>
            <TooltipTrigger asChild>
              <span className="shrink-0">
                <ThemeToggle />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={10} className="font-mono text-[9px] uppercase tracking-widest font-bold px-2 py-1 bg-background/90 backdrop-blur-md border-border/40 shadow-xl">
              Theme
            </TooltipContent>
          </Tooltip>

          <DockItem title="Switch to Sidebar" onClick={onSwitchToSidebar} className="text-muted-foreground hover:text-foreground">
            <PanelLeft className="size-4" />
          </DockItem>
        </div>
      </TooltipProvider>
    </div>
  );
}

// ── Shared popover wrapper ────────────────────────────────────────────────────

function DockPopover({
  title, icon, isActive, open, onOpenChange, badge, children,
}: {
  title: string;
  icon: React.ReactNode;
  isActive?: boolean;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  badge?: number;
  children: React.ReactNode;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button className={cn(
              "relative flex items-center justify-center size-9 rounded-xl transition-colors duration-200 focus:outline-none shrink-0",
              isActive ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25" : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
            )}>
              {icon}
              {isActive && <span className="absolute -bottom-1 w-1 h-1 rounded-full bg-primary-foreground/50" />}
              {badge !== undefined && badge > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white shadow-sm animate-pulse">{badge}</span>
              )}
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={10} className="font-mono text-[9px] uppercase tracking-widest font-bold px-2 py-1 bg-background/90 backdrop-blur-md border-border/40 shadow-xl">
          {title}
        </TooltipContent>
      </Tooltip>
      <PopoverContent side="top" sideOffset={12} className="w-44 p-2 bg-background/95 backdrop-blur-md border border-border/40 shadow-2xl rounded-xl">
        <div className="flex flex-col gap-0.5">{children}</div>
      </PopoverContent>
    </Popover>
  );
}

function PopoverLink({ to, active, onClick, children }: { to: string; active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <Link to={to} onClick={onClick} className={cn(
      "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors",
      active ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
    )}>
      {children}
    </Link>
  );
}

// ── DockItem ──────────────────────────────────────────────────────────────────

interface DockItemProps {
  title: string;
  url?: string;
  isActive?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  isLogo?: boolean;
}

function DockItem({ title, url, isActive, onClick, children, className, isLogo }: DockItemProps) {
  const content = (
    <div className={cn(
      "relative flex items-center justify-center size-9 rounded-xl transition-colors duration-200",
      isActive ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25" : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
      isLogo && "molt-brand-surface text-primary hover:text-primary",
      className
    )}>
      {children}
      {isActive && <span className="absolute -bottom-1 w-1 h-1 rounded-full bg-primary-foreground/50" />}
    </div>
  );
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {url ? (
          <Link to={url} onClick={onClick} className="focus:outline-none shrink-0">{content}</Link>
        ) : (
          <button onClick={onClick} className="focus:outline-none shrink-0">{content}</button>
        )}
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={10} className="font-mono text-[9px] uppercase tracking-widest font-bold px-2 py-1 bg-background/90 backdrop-blur-md border-border/40 shadow-xl">
        {title}
      </TooltipContent>
    </Tooltip>
  );
}
